import { RenovationInputs, RenovationCosts, CostItem, QualityLevel } from '../types';
import { COST_REFERENCES } from '../constants';

export const calculateRenovationCosts = (
  inputs: RenovationInputs,
  quality: QualityLevel
): RenovationCosts => {
  const getCost = (item: string): number => {
    return COST_REFERENCES[item]?.[quality] || 0;
  };
  
  // Estimate wall area if not provided
  const geschaetzteWandflaeche = inputs.wandflaeche > 0 
    ? inputs.wandflaeche 
    : (inputs.wohnflaeche * 2.5) + (inputs.anzahlZimmer * 10);
  
  const gewerkeKosten: CostItem[] = [
    { label: "Bad / Sanitär", value: inputs.anzahlBad * getCost('bad') },
    { label: "Küche", value: inputs.anzahlKueche * getCost('kueche') },
    { label: "Bodenbelag", value: inputs.bodenErneuern ? inputs.bodenflaeche * getCost('boden') : 0 },
    { label: "Fliesenboden", value: inputs.fliesenbodenflaeche > 0 ? inputs.fliesenbodenflaeche * getCost('fliesenboden') : 0 },
    { label: "Elektrik", value: inputs.elektrikErneuern ? inputs.wohnflaeche * getCost('elektrik') : 0 },
    { label: "Heizkörper", value: inputs.anzahlHeizkoerper * getCost('heizkoerper') },
    { label: "Heizung", value: inputs.heizungErneuern ? getCost('heizung_komplett') : 0 },
    { label: "Wasserleitungen Bad", value: inputs.wasserleitungenBadErneuern ? inputs.anzahlBad * getCost('wasserleitungen') : 0 },
    { label: "Wasserleitungen Küche", value: inputs.wasserleitungenKuecheErneuern ? inputs.anzahlKueche * getCost('wasserleitungen') : 0 },
    { label: "Türen", value: inputs.anzahlTueren * getCost('tueren') },
    { label: "Fenster", value: inputs.anzahlFenster * getCost('fenster') },
    { label: "Rollos", value: inputs.anzahlRollos * getCost('rollos') },
    { label: "Malerarbeiten", value: inputs.malenErneuern ? geschaetzteWandflaeche * getCost('malen') : 0 },
    { label: "Fassade", value: inputs.fassadenflaeche * getCost('fassade') },
    { label: "Dach", value: inputs.dachflaeche * getCost('dach') },
  ];
  
  const subtotalGewerke = gewerkeKosten.reduce((acc, item) => acc + (item.value || 0), 0);
  
  const planungKostenValue = subtotalGewerke * (inputs.planungProzent / 100);
  const nebenkosten: CostItem[] = [
    { label: "Abriss & Entsorgung", value: inputs.abrissKosten },
    { label: `Planung (${inputs.planungProzent}%)`, value: planungKostenValue },
    { label: "Statik / Gutachten", value: inputs.statikKosten },
  ];
  
  const subtotalNebenkosten = nebenkosten.reduce((acc, item) => acc + (item.value || 0), 0);
  
  const costItems = [...gewerkeKosten, ...nebenkosten];
  const subtotal = subtotalGewerke + subtotalNebenkosten;
  const risikoValue = subtotal * (inputs.risikozuschlag / 100);
  const total = subtotal + risikoValue;
  const kostenProQm = inputs.wohnflaeche > 0 ? total / inputs.wohnflaeche : 0;
  
  return {
    costItems,
    subtotalGewerke,
    subtotalNebenkosten,
    subtotal,
    total,
    geschaetzteWandflaeche,
    risikoValue,
    kostenProQm
  };
};

export const getReferenceTableData = () => {
  return [
    { 
      label: "Bad / Sanitär (€/Stk.)", 
      basis: 7000, 
      mittel: 12000, 
      premium: "18.000+", 
      notes: "Inkl. Objekte, Fliesen, Armaturen & Arbeit." 
    },
    { 
      label: "Küche (€/Stk.)", 
      basis: 3000, 
      mittel: 6000, 
      premium: "15.000+", 
      notes: "Inkl. Geräte, Anschlüsse & Montage." 
    },
    { 
      label: "Bodenbelag (€/m²)", 
      basis: 40, 
      mittel: 75, 
      premium: "130+", 
      notes: "Basis: Laminat/Vinyl. Mittel: Gutes Vinyl. Premium: Parkett." 
    },
    { 
      label: "Fliesenboden (€/m²)", 
      basis: 60, 
      mittel: 80, 
      premium: "100+", 
      notes: "Verlegung von Fliesen. Basis: Standard. Premium: Großformat." 
    },
    { 
      label: "Elektrik (€/m² Wohnfl.)", 
      basis: 90, 
      mittel: 120, 
      premium: "160+", 
      notes: "Inkl. Kasten. Premium mit Smarthome-Vorbereitung." 
    },
    { 
      label: "Wasserleitungen (€/Anschl.)", 
      basis: 3000, 
      mittel: 4000, 
      premium: "5.000+", 
      notes: "Pro Bad/Küche. Erneuerung ab Steigstrang." 
    },
    { 
      label: "Heizkörper (€/Stk.)", 
      basis: 350, 
      mittel: 550, 
      premium: "900+", 
      notes: "Inkl. Montage & Thermostat. Premium: Designheizkörper." 
    },
    { 
      label: "Heizung (€/Whg.)", 
      basis: 7000, 
      mittel: 9000, 
      premium: "12.000+", 
      notes: "Austausch einer Gasetagenheizung (Brennwerttherme)." 
    },
    { 
      label: "Türen (€/Stk.)", 
      basis: 400, 
      mittel: 650, 
      premium: "1.000+", 
      notes: "Inkl. Zarge & Einbau. Basis: CPL. Premium: Echtholz." 
    },
    { 
      label: "Fenster (€/Stk.)", 
      basis: 700, 
      mittel: 1100, 
      premium: "1.600+", 
      notes: "3-fach-Verglasung. Basis: Kunststoff. Premium: Holz-Alu." 
    },
    { 
      label: "Rollläden (€/Stk.)", 
      basis: 250, 
      mittel: 450, 
      premium: "700+", 
      notes: "Basis: Gurt. Mittel: E-Motor. Premium: Funk/Raffstore." 
    },
    { 
      label: "Malerarbeiten (€/m² Wand)", 
      basis: 25, 
      mittel: 35, 
      premium: "55+", 
      notes: "Wand-/Deckenfläche. Basis: Raufaser. Premium: Vlies/Spachtel." 
    },
    { 
      label: "Fassade (WDVS, €/m²)", 
      basis: 120, 
      mittel: 170, 
      premium: "250+", 
      notes: "Anteilige Kosten. Basis: Polystyrol. Premium: Mineralwolle." 
    },
    { 
      label: "Dach (inkl. Dämmung, €/m²)", 
      basis: 180, 
      mittel: 260, 
      premium: "350+", 
      notes: "Anteilige Kosten. Energetische Sanierung." 
    },
  ];
};